import React, { useState, useEffect } from 'react';
import { Bell, Heart, MessageCircle, UserPlus, Calendar, Package, Check, Trash2, Settings, Filter, RefreshCw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { notificationsService, Notification } from '../services/notificationsService';
import WarningDetailModal, { WarningData } from './WarningDetailModal';

const NotificationsView: React.FC = () => {
  const { user } = useAuth();
  const { select, locale } = useLanguage();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [markingAllAsRead, setMarkingAllAsRead] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedNotifications, setSelectedNotifications] = useState<Set<string>>(new Set());
  const [notificationStats, setNotificationStats] = useState<Record<string, { total: number; unread: number }>>({});
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [selectedViolation, setSelectedViolation] = useState<WarningData | null>(null);

  useEffect(() => {
    if (user) {
      void loadNotifications();
      void loadNotificationStats();
      
      // Subscribe to real-time notifications
      const subscription = notificationsService.subscribeToNotifications(
        user.id,
        (newNotification) => {
          setNotifications(prev => [newNotification, ...prev]);
          void loadNotificationStats(); // Update stats
        }
      );

      return () => {
        subscription.unsubscribe();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, filter, typeFilter]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      setError('');
      
      const data = await notificationsService.getUserNotifications({
        unreadOnly: filter === 'unread',
        type: typeFilter === 'all' ? undefined : typeFilter,
        limit: 50
      });
      
      setNotifications(data);
    } catch (err) {
      setError(select({ es: 'Error al cargar las notificaciones', en: 'Error loading notifications' }));
      console.error('Error loading notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadNotificationStats = async () => {
    try {
      const stats = await notificationsService.getNotificationsByType();
      setNotificationStats(stats);
    } catch (err) {
      console.error('Error loading notification stats:', err);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadNotifications();
    await loadNotificationStats();
    setRefreshing(false);
  };

  const markAsRead = async (notificationId: string) => {
    try {
      await notificationsService.markAsRead(notificationId);
      setNotifications(prev => prev.map(notification => 
        notification.id === notificationId 
          ? { ...notification, is_read: true }
          : notification
      ));
      loadNotificationStats();
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      setMarkingAllAsRead(true);
      await notificationsService.markAllAsRead();
      setNotifications(prev => prev.map(notification => ({ ...notification, is_read: true })));
      loadNotificationStats();
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    } finally {
      setMarkingAllAsRead(false);
    }
  };

  const deleteNotification = async (notificationId: string) => {
    try {
      await notificationsService.deleteNotification(notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      setSelectedNotifications(prev => {
        const newSet = new Set(prev);
        newSet.delete(notificationId);
        return newSet;
      });
      loadNotificationStats();
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  };

  const handleBatchDelete = async () => {
    if (selectedNotifications.size === 0) return;
    
    try {
      await notificationsService.deleteMultipleNotifications(Array.from(selectedNotifications));
      setNotifications(prev => prev.filter(n => !selectedNotifications.has(n.id)));
      setSelectedNotifications(new Set());
      loadNotificationStats();
    } catch (err) {
      console.error('Error deleting notifications:', err);
    }
  };

  const handleBatchMarkAsRead = async () => {
    if (selectedNotifications.size === 0) return;
    
    try {
      await notificationsService.markMultipleAsRead(Array.from(selectedNotifications));
      setNotifications(prev => prev.map(notification => 
        selectedNotifications.has(notification.id)
          ? { ...notification, is_read: true }
          : notification
      ));
      setSelectedNotifications(new Set());
      loadNotificationStats();
    } catch (err) {
      console.error('Error marking notifications as read:', err);
    }
  };

  const toggleSelectNotification = (notificationId: string) => {
    setSelectedNotifications(prev => {
      const newSet = new Set(prev);
      if (newSet.has(notificationId)) {
        newSet.delete(notificationId);
      } else {
        newSet.add(notificationId);
      }
      return newSet;
    });
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'like':
        return <Heart className="text-red-500" size={20} />;
      case 'comment':
        return <MessageCircle className="text-blue-500" size={20} />;
      case 'follow':
        return <UserPlus className="text-purple-500" size={20} />;
      case 'collaboration':
        return <Calendar className="text-green-500" size={20} />;
      case 'sale':
        return <Package className="text-orange-500" size={20} />;
      case 'event':
        return <Calendar className="text-indigo-500" size={20} />;
      case 'system':
        return <Bell className="text-gray-500" size={20} />;
      default:
        return <Bell className="text-gray-500" size={20} />;
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return select({ es: 'Ahora', en: 'Now' });
    if (diffInMinutes < 60) return `${diffInMinutes}m`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h`;
    if (diffInMinutes < 10080) return `${Math.floor(diffInMinutes / 1440)}d`;
    return date.toLocaleDateString(locale);
  };

  const isModerationNotification = (notification: Notification) => {
    const metadata = notification.metadata || {};
    const action = typeof metadata.action === 'string' ? metadata.action.toLowerCase() : '';
    const itemType = typeof metadata.item_type === 'string' ? metadata.item_type.toLowerCase() : '';
    const category = typeof metadata.category === 'string' ? metadata.category.toLowerCase() : '';

    if (metadata.moderation === true) return true;
    if (itemType === 'post' || itemType === 'comment') return true;
    if (category === 'moderation') return true;

    const actionKeywords = ['warn', 'warning', 'suspend', 'suspended', 'ban', 'banned', 'rejected', 'hidden', 'removed', 'delete', 'deleted'];
    if (actionKeywords.some(keyword => action.includes(keyword))) return true;

    return false;
  };

  const mapNotificationToWarningData = (notification: Notification): WarningData => {
    const metadata = notification.metadata || {};
    const itemType = typeof metadata.item_type === 'string' ? metadata.item_type : '';

    const violationType =
      metadata.violation_type ||
      metadata.category ||
      select({ es: 'Incumplimiento de normas de comunidad', en: 'Community guidelines violation' });

    const date = metadata.moderated_at || notification.created_at;

    const contentSnapshot =
      metadata.content_excerpt ||
      metadata.comment_content ||
      metadata.post_title ||
      metadata.post_content ||
      notification.message;

    const violationHighlight = metadata.violation_excerpt || metadata.highlight;

    const rawRules = metadata.rules || metadata.norms;
    let rules: { code?: string; label: string }[] | undefined;
    if (Array.isArray(rawRules)) {
      rules = rawRules
        .map((rule: unknown) => {
          if (typeof rule === 'string') {
            return { label: rule };
          }
          if (rule && typeof rule === 'object') {
            const ruleObj = rule as Record<string, unknown>;
            const code = typeof ruleObj.code === 'string' ? ruleObj.code : undefined;
            const labelSource =
              typeof ruleObj.label === 'string'
                ? ruleObj.label
                : typeof ruleObj.name === 'string'
                ? ruleObj.name
                : typeof ruleObj.title === 'string'
                ? ruleObj.title
                : undefined;
            if (typeof labelSource === 'string') {
              return { code, label: labelSource };
            }
          }
          return null;
        })
        .filter(Boolean) as { code?: string; label: string }[];
    }

    const explanation =
      (typeof metadata.reason === 'string' && metadata.reason) ||
      (typeof metadata.moderation_reason === 'string' && metadata.moderation_reason) ||
      (typeof metadata.description === 'string' && metadata.description) ||
      '';

    const visibility = typeof metadata.visibility === 'string' ? metadata.visibility : '';

    const currentConsequences =
      metadata.consequences ||
      (visibility === 'removed'
        ? select({
            es: 'Este contenido ha sido eliminado y ya no es visible en la plataforma.',
            en: 'This content has been removed and is no longer visible on the platform.',
          })
        : visibility === 'hidden'
        ? select({
            es: 'Este contenido ha sido ocultado para otros usuarios.',
            en: 'This content has been hidden from other users.',
          })
        : visibility === 'limited'
        ? select({
            es: 'La visibilidad de este contenido ha sido limitada.',
            en: 'The visibility of this content has been limited.',
          })
        : notification.message);

    const futureConsequences =
      metadata.future_consequences ||
      select({
        es: 'Repetir este comportamiento puede conllevar más advertencias o la suspensión de tu cuenta.',
        en: 'Repeating this behaviour may lead to more warnings or suspension of your account.',
      });

    const warningsCount = typeof metadata.warnings_count === 'number' ? metadata.warnings_count : undefined;
    const warningsLimit = typeof metadata.warnings_limit === 'number' ? metadata.warnings_limit : undefined;
    const accountStatus =
      (typeof metadata.account_status === 'string' && metadata.account_status) ||
      (typeof metadata.profile_status === 'string' && metadata.profile_status) ||
      undefined;

    const referenceId =
      (typeof metadata.ticket_number === 'string' && metadata.ticket_number) ||
      (typeof metadata.report_id === 'string' && metadata.report_id) ||
      (typeof metadata.moderation_id === 'string' && metadata.moderation_id) ||
      notification.id;

    const appealProcess =
      (typeof metadata.appeal_instructions === 'string' && metadata.appeal_instructions) ||
      select({
        es: 'Si no estás de acuerdo con esta decisión, puedes enviar una apelación desde el centro de ayuda explicando por qué consideras que se trata de un error.',
        en: 'If you disagree with this decision, you can submit an appeal from the help center explaining why you believe this is a mistake.',
      });

    const historyUrl = typeof metadata.history_url === 'string' ? metadata.history_url : undefined;
    const normsUrl = typeof metadata.norms_url === 'string' ? metadata.norms_url : undefined;

    const moderatedByUsername =
      (typeof metadata.moderated_by_username === 'string' && metadata.moderated_by_username) ||
      (typeof metadata.moderated_by === 'string' && metadata.moderated_by) ||
      undefined;

    const contentTypeLabel = (() => {
      const lowered = itemType.toLowerCase();
      if (lowered === 'comment') {
        return select({ es: 'Comentario', en: 'Comment' });
      }
      if (lowered === 'post') {
        return select({ es: 'Publicación', en: 'Post' });
      }
      return select({ es: 'Contenido', en: 'Content' });
    })();

    const violationTypeString = String(violationType);
    const dateString = String(date);
    const contentSnapshotString = String(contentSnapshot || '');

    return {
      violation_type: violationTypeString,
      violation_date: dateString,
      content_snapshot: contentSnapshotString,
      violation_highlight: violationHighlight ? String(violationHighlight) : undefined,
      rules,
      explanation: explanation || undefined,
      consequences: String(currentConsequences || ''),
      future_consequences: futureConsequences ? String(futureConsequences) : undefined,
      appeal_process: String(appealProcess),
      reference_id: referenceId ? String(referenceId) : undefined,
      warnings_count: warningsCount,
      warnings_limit: warningsLimit,
      account_status: accountStatus ? String(accountStatus) : undefined,
      history_url: historyUrl,
      norms_url: normsUrl,
      moderated_by: moderatedByUsername,
      content_type_label: contentTypeLabel,
    };
  };

  const openModerationWarning = async (notification: Notification) => {
    const warningData = mapNotificationToWarningData(notification);
    setSelectedViolation(warningData);
    setShowWarningModal(true);

    try {
      const updatedMetadata = await notificationsService.recordModerationModalView(notification.id, notification.metadata);
      setNotifications(prev =>
        prev.map(n =>
          n.id === notification.id
            ? { ...n, metadata: updatedMetadata }
            : n
        )
      );
    } catch (err) {
      console.error('Error recording moderation modal view:', err);
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }

    if (isModerationNotification(notification)) {
      await openModerationWarning(notification);
      return;
    }

    if (notification.related_post_id) {
      console.log('Navigate to post:', notification.related_post_id);
    } else if (notification.related_user_id && notification.type === 'follow') {
      const event = new CustomEvent('navigate', {
        detail: {
          tab: 'profile',
          userId: notification.related_user_id
        }
      });
      window.dispatchEvent(event);
    } else if (notification.related_collaboration_id) {
      const event = new CustomEvent('navigate', {
        detail: { tab: 'collaborations' }
      });
      window.dispatchEvent(event);
    } else if (notification.related_event_id) {
      const event = new CustomEvent('navigate', {
        detail: { tab: 'events' }
      });
      window.dispatchEvent(event);
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;
  const typeOptions = [
    { value: 'all', label: select({ es: 'Todas', en: 'All' }), count: notifications.length },
    { value: 'like', label: select({ es: 'Likes', en: 'Likes' }), count: notificationStats.like?.total || 0 },
    { value: 'comment', label: select({ es: 'Comentarios', en: 'Comments' }), count: notificationStats.comment?.total || 0 },
    { value: 'follow', label: select({ es: 'Seguidores', en: 'Followers' }), count: notificationStats.follow?.total || 0 },
    { value: 'collaboration', label: select({ es: 'Colaboraciones', en: 'Collaborations' }), count: notificationStats.collaboration?.total || 0 },
    { value: 'sale', label: select({ es: 'Ventas', en: 'Sales' }), count: notificationStats.sale?.total || 0 },
    { value: 'event', label: select({ es: 'Eventos', en: 'Events' }), count: notificationStats.event?.total || 0 },
    { value: 'system', label: select({ es: 'Sistema', en: 'System' }), count: notificationStats.system?.total || 0 },
  ];

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="bg-gray-200 h-20 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Bell className="text-purple-600" size={24} />
            {select({ es: 'Notificaciones', en: 'Notifications' })}
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white text-sm font-medium px-2 py-1 rounded-full">
                {unreadCount}
              </span>
            )}
          </h1>
          <p className="text-gray-600 mt-1">{select({ es: 'Mantente al día con tu actividad', en: 'Stay on top of your activity' })}</p>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            title={select({ es: 'Actualizar notificaciones', en: 'Refresh notifications' })}
          >
            <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
          </button>
          
          <button
            onClick={() => setFilter(filter === 'all' ? 'unread' : 'all')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === 'unread'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {filter === 'all'
              ? select({ es: 'Solo no leídas', en: 'Only unread' })
              : select({ es: 'Todas', en: 'All' })}
          </button>
          
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              disabled={markingAllAsRead}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              <Check size={16} />
              {markingAllAsRead
                ? select({ es: 'Marcando...', en: 'Marking...' })
                : select({ es: 'Marcar todas como leídas', en: 'Mark all as read' })}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={16} className="text-gray-500" />
          <span className="font-medium text-gray-700">{select({ es: 'Filtros', en: 'Filters' })}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {typeOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => setTypeFilter(option.value)}
              className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                typeFilter === option.value
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {option.label} ({option.count})
            </button>
          ))}
        </div>
      </div>

      {/* Batch Actions */}
      {selectedNotifications.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <span className="text-blue-700">
              {selectedNotifications.size}{' '}
              {selectedNotifications.size !== 1
                ? select({ es: 'notificaciones seleccionadas', en: 'notifications selected' })
                : select({ es: 'notificación seleccionada', en: 'notification selected' })}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={handleBatchMarkAsRead}
                className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
              >
                {select({ es: 'Marcar como leídas', en: 'Mark as read' })}
              </button>
              <button
                onClick={handleBatchDelete}
                className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
              >
                {select({ es: 'Eliminar', en: 'Delete' })}
              </button>
              <button
                onClick={() => setSelectedNotifications(new Set())}
                className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 transition-colors"
              >
                {select({ es: 'Cancelar', en: 'Cancel' })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notifications List */}
      <div className="space-y-3">
        {notifications.length > 0 ? (
          notifications.map((notification) => (
            <div
              key={notification.id}
              className={`bg-white border rounded-lg p-4 hover:shadow-md transition-all cursor-pointer ${
                !notification.is_read 
                  ? 'border-purple-200 bg-purple-50/30' 
                  : 'border-gray-200'
              } ${
                selectedNotifications.has(notification.id) ? 'ring-2 ring-purple-500 ring-opacity-50' : ''
              }`}
              onClick={() => handleNotificationClick(notification)}
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={selectedNotifications.has(notification.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      toggleSelectNotification(notification.id);
                    }}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <div className="flex-shrink-0">
                    {getNotificationIcon(notification.type)}
                  </div>
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className={`font-semibold ${
                      !notification.is_read ? 'text-gray-900' : 'text-gray-700'
                    }`}>
                      {notification.title}
                    </h3>
                    
                    <div className="flex items-center gap-2 ml-4">
                      <span className="text-sm text-gray-500">
                        {formatTimeAgo(notification.created_at)}
                      </span>
                      
                     {!notification.is_read && (
                       <button
                         onClick={(e) => {
                           e.stopPropagation();
                           markAsRead(notification.id);
                         }}
                          className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                          title={select({ es: 'Marcar como leída', en: 'Mark as read' })}
                        >
                          <Check size={16} />
                        </button>
                      )}
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteNotification(notification.id);
                        }}
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                        title={select({ es: 'Eliminar notificación', en: 'Delete notification' })}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  
                  <p className="text-gray-700 mb-2">{notification.message}</p>
                  
                  {/* Related content preview */}
                  {notification.related_user && (
                    <div className="flex items-center gap-2 mt-3">
                      <img
                        src={notification.related_user.avatar_url || 'https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=32&h=32&fit=crop'}
                        alt={notification.related_user.full_name}
                        className="w-6 h-6 rounded-full object-cover"
                      />
                      <span className="text-sm text-gray-600">
                        @{notification.related_user.username}
                      </span>
                      {notification.related_user.is_premium && (
                        <span className="text-yellow-500 text-xs">✨</span>
                      )}
                    </div>
                  )}

                  {notification.related_post && (
                    <div className="flex items-center gap-2 mt-3 p-2 bg-gray-50 rounded-lg">
                      {notification.related_post.media_files?.[0] && (
                        <img
                          src={notification.related_post.media_files[0].url}
                          alt={notification.related_post.title}
                          className="w-8 h-8 object-cover rounded"
                        />
                      )}
                      <span className="text-sm text-gray-600 truncate">
                        {notification.related_post.title}
                      </span>
                    </div>
                  )}
                  
                  {!notification.is_read && (
                    <div className="absolute top-4 right-4 w-2 h-2 bg-purple-500 rounded-full"></div>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-12">
            <Bell size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {filter === 'unread'
                ? select({ es: 'No hay notificaciones sin leer', en: 'No unread notifications' })
                : select({ es: 'No hay notificaciones', en: 'No notifications' })}
            </h3>
            <p className="text-gray-500">
              {filter === 'unread' 
                ? select({ es: 'Todas tus notificaciones están al día', en: 'You are all caught up' })
                : select({ es: 'Cuando tengas actividad nueva aparecerá aquí', en: 'New activity will show up here' })
              }
            </p>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="mt-8 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Settings size={20} />
          {select({ es: 'Configurar notificaciones', en: 'Configure notifications' })}
        </h2>
        <p className="text-gray-600 mb-4">
          {select({ es: 'Personaliza qué notificaciones quieres recibir para mantenerte informado sin saturarte.', en: 'Choose which notifications you want to receive so you stay informed without overload.' })}
        </p>
        <button 
          onClick={() => {
            // Navigate to settings with notifications tab
            const settingsEvent = new CustomEvent('openSettings', { detail: { tab: 'notifications' } });
            window.dispatchEvent(settingsEvent);
          }}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          {select({ es: 'Ir a configuración', en: 'Go to settings' })}
        </button>
      </div>
      {/* Warning Detail Modal */}
      {showWarningModal && selectedViolation && (
        <WarningDetailModal
          isOpen={showWarningModal}
          onClose={() => setShowWarningModal(false)}
          data={selectedViolation}
        />
      )}
    </div>
  );
};

export default NotificationsView;
